import React, { useEffect, useState, useCallback } from 'react';
import { BasicTableRow, getBasicTableData, Pagination } from 'api/clinicsGet.api';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { ColumnsType } from 'antd/es/table';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';
import { defineColorByPriority } from '@app/utils/utils';
import { notificationController } from 'controllers/notificationController';
import { Status } from '@app/components/profile/profileCard/profileFormNav/nav/payments/paymentHistory/Status/Status';
import { useMounted } from '@app/hooks/useMounted';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { getAllPushNotificationsAction, deletePushNotificationAction } from '@app/store/slices/userSlice';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 20,
};

export const BasicTable: React.FC = () => {
  const [tableData, setTableData] = useState<{ data: BasicTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { t } = useTranslation();
  const { isMounted } = useMounted();
  let navigate = useNavigate();

  const dispatch = useAppDispatch();

  useEffect(() => {
    setTableData((prev) => ({ ...prev, loading: true }));
    dispatch(getAllPushNotificationsAction({}))
      .unwrap()
      .then((resp) => {
        setTableData({ 
          data: resp.data || [], 
          pagination: {
            current: resp.page || 1,
            pageSize: resp.pageSize || 20,
            total: resp.total || 0
          },
          loading: false 
        });
      }).catch((err) => {
        setTableData((prev) => ({ ...prev, loading: false }));
        notificationController.error({ message: err.message });
      })
  }, []);

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize
      };
      dispatch(getAllPushNotificationsAction(params))
        .unwrap()
        .then((resp) => {
          if (isMounted.current) {
            setTableData({ 
              data: resp.data || [], 
              pagination: {
                current: resp.page || 1,
                pageSize: resp.pageSize || 20,
                total: resp.total || 0
              },
              loading: false 
            });
          }
        }).catch((err) => {
          if (isMounted.current) {
            setTableData((prev) => ({ ...prev, loading: false }));
            notificationController.error({ message: err.message });
          }
        });
    },
    [isMounted, dispatch],
  );

  const handleTableChange = (pagination: Pagination) => {
    fetch(pagination);
  };

  const handleDeleteRow = (rowId: string) => {
    if (window.confirm('Are you sure you want to delete this push notification?')) {
      dispatch(deletePushNotificationAction(rowId))
        .unwrap()
        .then(() => {
          notificationController.success({ message: 'Push notification deleted successfully' });
          setTableData({
            ...tableData,
            data: tableData.data.filter((item) => item._id !== rowId),
            pagination: {
              ...tableData.pagination,
              total: tableData.pagination.total ? tableData.pagination.total - 1 : tableData.pagination.total,
            },
          });
        })
        .catch((err) => {
          notificationController.error({ message: err.message });
        });
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text: string) => <span>{text?.substring(0, 100)}{text?.length > 100 ? '...' : ''}</span>,
    },
    {
      title: 'Template Type',
      dataIndex: 'templateType',
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: 'Is Template',
      key: 'isTemplate',
      dataIndex: 'isTemplate',
      render: (isTemplate: boolean) => (
        <BaseRow gutter={[10, 10]}>
          <BaseCol>
            <Status color={isTemplate ? 'var(--success-color)' : 'var(--error-color)'} text={isTemplate ? 'YES' : 'NO'} />
          </BaseCol>
        </BaseRow>
      ),
    },
    {
      title: t('tables.actions'),
      dataIndex: 'actions',
      width: '20%',
      render: (text: string, record: any) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              onClick={() => {
                navigate('/admin/push-notifications/edit/' + record?._id)
              }}
            >
              {t('common.edit')}
            </BaseButton>

            <BaseButton 
              type="default" 
              danger
              onClick={() => handleDeleteRow(record._id)}
            >
              {t('common.delete')}
            </BaseButton>
          </BaseSpace>
        );
      },
    },
  ];

  return (
    <BaseTable
      columns={columns}
      dataSource={tableData.data}
      pagination={tableData.pagination}
      loading={tableData.loading}
      onChange={handleTableChange}
      scroll={{ x: 800 }}
      bordered
    />
  );
};
